import { AttachInternals, Event, EventEmitter, Component, Prop, State, Watch, h } from '@stencil/core';

@Component({
  tag: 'spw-text-field',
  styleUrl: 'spw-text-field.scss',
  formAssociated: true,
  shadow: true,
})
export class SpwTextField {
  /** Événement émis lorsque la valeur de l'input change */
  @Event() valueChanged: EventEmitter<string>;

  @State() internalValue: string = '';

  /** Valeur actuelle du champ de saisie, mutable et reflétée dans le DOM */
  @Prop({ mutable: true, reflect: true }) value?: string;
  /** Type d'input à rendre, par exemple, "text", "password", etc. */
  @Prop() type?: string = 'text';
  /** Texte à afficher au-dessus du champ de saisie */
  @Prop() label: string = '';
  /** Texte du placeholder pour le champ de saisie */
  @Prop() placeholder: string;
  /** Si `true`, le champ de saisie est désactivé et ne peut pas être utilisé */
  @Prop() disabled?: boolean;
  /** Attribut `name` de l'élément input */
  @Prop() name: string;
  /** Longueur minimale pour la valeur de l'input */
  @Prop() minLength?: number;
  /** Longueur maximale pour la valeur de l'input */
  @Prop() maxLength?: number;
  /** Indique si l'input est un champ de recherche */
  @Prop() isSearch?: boolean = false;
  /** Indique si le champ peut être effacé avec un bouton */
  @Prop() isClear?: boolean = false;
  /** Indique si l'input est requis */
  @Prop() required?: boolean = false;
  /** Spécifie la taille du champ de saisie : 'large' ou 'medium' */
  @Prop() size?: 'large' | 'medium' = 'medium';
  /** Indicateur à afficher à droite du champ de saisie */
  @Prop() indicator?: string;
  /** Texte d'assistance à afficher sous le champ de saisie */
  @Prop() assistiveText?: string;
  /** Message d'erreur à afficher sous le champ */
  @Prop() error?: string = '';
  /** Message d'indication (hint) à afficher sous le champ */
  @Prop() hint?: string = '';
  /** Message de succès à afficher sous le champ */
  @Prop() success?: string = '';
  /** Affiche une icône d'erreur si vrai */
  @Prop() showErrorIcon?: boolean = true;
  /** Affiche une icône d'indication si vrai */
  @Prop() showHintIcon?: boolean = true;
  /** Affiche une icône de succès si vrai */
  @Prop() showSuccessIcon?: boolean = true;

  @State() isClearIconVisible: boolean = false;

  @AttachInternals() internals: ElementInternals;

  @Watch('value')
  valueChangedHandler(newValue: string) {
    this.internalValue = newValue;
    this.internals.setFormValue(this.internalValue);
    this.valueChanged.emit(this.internalValue);
  }

  componentWillLoad() {
    if (this.value) {
      this.internalValue = this.value;
    }
  }

  private handleChange(event) {
    this.internalValue = event.target.value;
    this.value = this.internalValue;
    this.isClearIconVisible = this.isClear && !!this.internalValue;
    this.internals.setFormValue(this.internalValue);
    this.valueChanged.emit(this.internalValue);
  }

  private handleClear() {
    this.internalValue = '';
    this.value = '';
    this.internals.setFormValue(this.internalValue);
    this.valueChanged.emit(this.internalValue);
    this.isClearIconVisible = false;
  }

  private renderErrorMessage() {
    if (this.error) {
      return (
        <spw-field-message variant="error" showIcon={this.showErrorIcon}>
          {this.error}
        </spw-field-message>
      );
    }
  }

  private renderHintMessage() {
    if (this.hint) {
      return (
        <spw-field-message variant="hint" showIcon={this.showHintIcon}>
          {this.hint}
        </spw-field-message>
      );
    }
  }

  private renderSuccessMessage() {
    if (this.success) {
      return (
        <spw-field-message variant="success" showIcon={this.showSuccessIcon}>
          {this.success}
        </spw-field-message>
      );
    }
  }

  private get elementClass(): { [className: string]: boolean } {
    return {
      'spw-text-field': true,
      [`spw-text-field--${this.size}`]: true,
      'spw-text-field--is-search': this.isSearch,
      'spw-text-field--is-error': !!this.error,
      'spw-text-field--is-clear': this.isClearIconVisible,
      'spw-text-field--disabled': this.disabled,
    };
  }

  render() {
    return (
      <div class={this.elementClass}>
        {this.label && <spw-field-label label={this.label} name={this.name} required={this.required}></spw-field-label>}
        {this.assistiveText && (
          <spw-field-message class="spw-tw-mb-2 -spw-tw-mt-1" variant="hint" showIcon={false}>
            {this.assistiveText}
          </spw-field-message>
        )}
        <div class="spw-text-field__container">
          <div class="spw-text-field__input-container">
            <input
              class="spw-text-field__input"
              type={this.type}
              value={this.internalValue}
              placeholder={this.placeholder}
              disabled={this.disabled}
              name={this.name}
              id={this.name}
              minLength={this.minLength}
              maxLength={this.maxLength}
              required={this.required}
              onInput={event => this.handleChange(event)}
            />
            {(this.isSearch || this.isClearIconVisible) && (
              <div class="spw-text-field__icons">
                {this.isClearIconVisible && (
                  <span class="spw-text-field__clear" onClick={() => this.handleClear()}>
                    <spw-icon class="spw-text-field__icon spw-text-field__icon--clear" icon="fa-xmark"></spw-icon>
                  </span>
                )}
                {this.isSearch && <spw-icon class="spw-text-field__icon spw-text-field__icon--search" icon="fa-magnifying-glass"></spw-icon>}
              </div>
            )}
          </div>
          {this.indicator && <div class="spw-text-field__indicator">{this.indicator}</div>}
        </div>
        {this.renderErrorMessage()}
        {this.renderHintMessage()}
        {this.renderSuccessMessage()}
      </div>
    );
  }
}
